import { useState, useCallback } from 'react';
import axios from 'axios';
import compressImage from './image_compressor';
import appAxios from '../components/axios';

const PRESIGN_URL = '/api/s3/cache/presign';

const isImageFile = (file) =>
  !!(file && (file.type || '').toLowerCase().startsWith('image/'));

/**
 * Presigned URL で R2 に直接ファイルをアップロードする React フック。
 * 画像は compressImage（HEIC→JPEG・5MB 圧縮）を通し、非画像はそのまま PUT する。
 * 既存の usePresignedImageUpload（イベント画像専用）は変更しない。
 *
 * @returns {{ upload: (file: File, options?: { signal?: AbortSignal, storageName?: string }) => Promise<string>, uploading: boolean }}
 *   upload - ファイルを受け取り、Shrine cache JSON 文字列を返す。AbortSignal で中断可能。
 *   uploading - いずれかのアップロードが進行中か
 */
const usePresignedFileUpload = () => {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (rawFile, options = {}) => {
    if (!rawFile) return null;

    const { signal, storageName = 'cache' } = options;
    setUploading(true);

    try {
      // 画像のみ圧縮。非画像はバイナリを壊さないよう素通し。
      const file = isImageFile(rawFile)
        ? await compressImage(rawFile)
        : rawFile;

      if (signal?.aborted) {
        const err = new Error('Upload aborted');
        err.name = 'CanceledError';
        err.code = 'ERR_CANCELED';
        throw err;
      }

      // Step 1: Rails から presigned URL を取得
      const presignRes = await appAxios.get(PRESIGN_URL, {
        params: { filename: file.name, type: file.type },
        signal,
      });
      const { url, fields, headers } = presignRes.data;

      // Step 2: クラウドに直接アップロード
      if (fields && Object.keys(fields).length > 0) {
        const formData = new FormData();
        Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);
        await axios.post(url, formData, { signal });
      } else {
        await axios.put(url, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream', ...headers },
          signal,
        });
      }

      // Step 3: Shrine cache JSON を構築
      const id = (fields?.key || new URL(url).pathname).split('/').pop();
      return JSON.stringify({
        id,
        storage: storageName,
        metadata: {
          filename: file.name,
          size: file.size,
          mime_type: file.type,
        },
      });
    } catch (error) {
      // Abort / Cancel は呼び出し側で中断扱いにする
      if (
        axios.isCancel?.(error) ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
      ) {
        throw error;
      }
      throw error;
    } finally {
      setUploading(false);
    }
  }, []);

  return { upload, uploading };
};

export default usePresignedFileUpload;
export { isImageFile };
